import { useLocation } from 'react-router-dom';

import { Modal } from '@/components/ui/Modal';

import styles from './SupportModal.module.css';

/** Kai Health help desk (knowledge base / help center). */
export const HELP_CENTER_URL = 'https://help.kaihealth.ai';

/** Pylon support request form. */
export const SUPPORT_FORM_URL =
  'https://kai-health.portal.usepylon.com/forms/kai-health-support-request';

interface SupportModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Help & Support sheet. The help desk lives on its own origin
 * (help.kaihealth.ai), so rather than embedding it in an iframe (which the
 * portal's frame policy blocks and which breaks SSO, deep links and the back
 * button) this sheet hands the user off to it in a new tab. The app stays
 * open underneath so they can come back to what they were doing.
 *
 * The current in-app route is passed along as a query param so a support
 * request can carry where the user was when they asked for help.
 */
export function SupportModal({ open, onClose }: SupportModalProps) {
  const location = useLocation();

  const requestUrl = withContext(SUPPORT_FORM_URL, location.pathname);

  return (
    <Modal
      open={open}
      onClose={onClose}
      fullScreenOnMobile
      title="Help & Support"
      subtitle="Search the help center or send the Kai Health team a request"
      labelledBy="support-modal-title"
    >
      <span id="support-modal-title" className={styles.srOnly}>
        Help and support
      </span>

      <div className={styles.options}>
        <a
          href={HELP_CENTER_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.option}
          onClick={onClose}
        >
          <span className={styles.optionTitle}>Browse the help center ↗</span>
          <span className={styles.optionBody}>
            Guides, FAQs and release notes for Kai Health.
          </span>
        </a>

        <a
          href={requestUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.option}
          onClick={onClose}
        >
          <span className={styles.optionTitle}>Submit a support request ↗</span>
          <span className={styles.optionBody}>
            Report an issue or ask a question. We reply by email.
          </span>
        </a>
      </div>

      <p className={styles.note}>
        Help opens in a new tab so you can keep working here.
      </p>
    </Modal>
  );
}

/**
 * Append the in-app route the user was on so the help desk form can record
 * it. The value is URL-encoded by URLSearchParams; the help desk ignores
 * unknown params, so this is safe even if the form does not map it.
 */
function withContext(base: string, path: string): string {
  const url = new URL(base);
  url.searchParams.set('app_path', path);
  return url.toString();
}
